import { format } from 'date-fns';
import { updateAmountOnItem, getDealComponentsSafe, getId } from './common';

const getVoucherTypeId = (type, amount) => {
  if (type === 'AP') {
    // is positive return PAYABLE (2)
    // is negative return PAYABLE CREDIT (4)
    return amount >= 0 ? 2 : 4;
  }

  // is positive return RECEIVABLE (1)
  // is negative return RECEIVABLE CREDIT (3)
  return amount >= 0 ? 1 : 3;
};

// modifies vouchers
const getOrCreateEditableVouchersByDealTransaction = (
  type,
  amount,
  contactId,
  accountId,
  vouchers,
) => {
  const voucherTypeId = getVoucherTypeId(type, amount);
  const editableVouchers = vouchers.filter(
    ({ ContactId, VoucherTypeId, AccountId, IsEditable }) =>
      getVoucherTypeId(type, amount) === VoucherTypeId &&
      ContactId === contactId &&
      AccountId === accountId &&
      IsEditable,
  );

  if (editableVouchers.length === 0) {
    const newVoucher = {
      Pending: true,
      VoucherId: getId(),
      ContactId: contactId,
      VoucherTypeId: voucherTypeId,
      AccountId: accountId,
      StatusId: 1,
      IsEditable: true,
      VoucherDate: format(new Date(), 'YYYY-MM-DD'),
    };

    vouchers.push(newVoucher);
    editableVouchers.push(newVoucher);
  }

  return editableVouchers;
};

const getAllDealTransactionsOnDeal = (dealId, dealTransactions) =>
  dealTransactions.filter(({ DealId }) => DealId === dealId);

const getVoucherAccount = (dealTransactionType, creditAccount, debitAccount) =>
  dealTransactionType === 'AP' ? creditAccount : debitAccount;

// TODO: check to make sure reduced correctly
const sumDtvts = dtvts => dtvts.reduce((sum, dtvt) => dtvt.Amount + sum, 0);

const getVoucherTransactionAccount = (
  dealTransactionType,
  creditAccount,
  debitAccount,
) => (dealTransactionType === 'AP' ? debitAccount : creditAccount);

const isDealTransactionVoucherd = (
  dealTransactionId,
  dealTransactionAmount,
  dtvts,
) => {
  const relatedDtvts = dtvts.filter(
    ({ DealTranId }) => DealTranId === dealTransactionId,
  );
  // sum amount of all related dtvts
  const relatedDtvtsSum = sumDtvts(relatedDtvts);

  return relatedDtvtsSum === dealTransactionAmount;
};

const getEditableVoucherTransactions = (
  voucherTransactions,
  dtvts,
  dealTransactionId,
) =>
  voucherTransactions.filter(({ VoucherTranId }) => {
    // get all dtvts on out voucher transaction
    const dtvtsOnVoucherTransaction = dtvts.filter(
      ({ VoucherTranId: dtvtVoucherTransactionId }) =>
        VoucherTranId === dtvtVoucherTransactionId,
    );

    // IF there is 1 dtvt on the voucherTransaction
    // AND the deal transaction id on that 1 dtvt is THE deal transaction id
    // OR there are no dtvts on the voucherTransaction
    // we know the voucher transaction is modifiable
    return (
      (dtvtsOnVoucherTransaction.length === 1 &&
        dtvtsOnVoucherTransaction[0].DealTranId === dealTransactionId) ||
      dtvtsOnVoucherTransaction.length === 0
    );
  });

// modifies voucherTransactions
// vouchers will usually be already filtered to just editable ones
const getOrCreateEditableVoucherTransactionsByDealTransaction = (
  dealTransactionId,
  dealTransactionType,
  dealTransactionCreditAccount,
  dealTransactionDebitAccount,
  dealTransactionCategory,
  dealTransactionAmount,
  voucherTransactionAccountId,
  editableVouchers,
  voucherTransactions,
  dtvts,
) => {
  const voucherIds = editableVouchers.map(({ VoucherId }) => VoucherId);
  const voucherTransactionsOnVouchers = voucherTransactions.filter(
    ({ VoucherId, AccountId }) =>
      voucherIds.includes(VoucherId) &&
      AccountId === voucherTransactionAccountId,
  );

  const editableVoucherTransactions = getEditableVoucherTransactions(
    voucherTransactionsOnVouchers,
    dtvts,
    dealTransactionId,
  );

  // IF there are no editable voucher transactions
  // ADD editable voucher transaction to first editable voucher
  if (editableVoucherTransactions.length === 0) {
    const newEditableVoucherTransaction = {
      Pending: true,
      VoucherTranId: getId(),
      VoucherId: editableVouchers[0].VoucherId,
      AccountId: voucherTransactionAccountId,
      CategoryId: dealTransactionCategory,
      Amount: dealTransactionAmount,
    };

    editableVoucherTransactions.push(newEditableVoucherTransaction);
    voucherTransactions.push(newEditableVoucherTransaction);
  }

  return editableVoucherTransactions;
};

const mapVoucherTransactions = ({
  dtvts,
  editableVoucherTransactions,
  DealTranId,
}) =>
  editableVoucherTransactions.filter(
    ({ VoucherTranId }) =>
      // find dtvt with voucher tran id and deal tran id
      !!dtvts.find(
        ({
          VoucherTranId: voucherTransactionIdOnDtvt,
          DealTranId: dealTransactionIdOnDtvt,
        }) =>
          VoucherTranId === voucherTransactionIdOnDtvt &&
          DealTranId === dealTransactionIdOnDtvt,
      ),
  );

const findDtvtToEdit = ({ dtvts, voucherTransactionToEdit, DealTranId }) =>
  dtvts.find(
    ({
      VoucherTranId: voucherTransactionIdOnDtvt,
      DealTranId: dealTransactionIdOnDtvt,
    }) =>
      voucherTransactionToEdit.VoucherTranId === voucherTransactionIdOnDtvt &&
      DealTranId === dealTransactionIdOnDtvt,
  );

const getVoucherTransactionsWithAmount = (voucherTransactions, amount) =>
  voucherTransactions.filter(
    ({ Amount: voucherTransactionAmount }) =>
      voucherTransactionAmount === amount,
  );

const voucherDealMap = dealMap => {
  const {
    deals,
    dealTransactions,
    dtvts,
    voucherTransactions,
    vouchers,
  } = getDealComponentsSafe(dealMap);

  deals.forEach(({ DealId }) => {
    const dealTransactionsOnDeal = getAllDealTransactionsOnDeal(
      DealId,
      dealTransactions,
    );

    dealTransactionsOnDeal.forEach(
      ({
        ContactId,
        TranType,
        CreditAccount,
        DebitAccount,
        Amount,
        Category,
        DealTranId,
      }) => {
        // check if deal transaction is already voucherd
        if (isDealTransactionVoucherd(DealTranId, Amount, dtvts)) {
          return;
        }

        const voucherAccountId = getVoucherAccount(
          TranType,
          CreditAccount,
          DebitAccount,
        );

        const editableVouchers = getOrCreateEditableVouchersByDealTransaction(
          TranType,
          Amount,
          ContactId,
          voucherAccountId,
          vouchers,
        );

        const voucherTransactionAccountId = getVoucherTransactionAccount(
          TranType,
          CreditAccount,
          DebitAccount,
        );

        const editableVoucherTransactions = getOrCreateEditableVoucherTransactionsByDealTransaction(
          DealTranId,
          TranType,
          CreditAccount,
          DebitAccount,
          Category,
          Amount,
          voucherTransactionAccountId,
          editableVouchers,
          voucherTransactions,
          dtvts,
        );

        const mappedEditableVoucherTransactions = mapVoucherTransactions({
          dtvts,
          editableVoucherTransactions,
          DealTranId,
        });

        // if one of the editable voucher transactions is already mapped to our deal transaction
        // update the dtvt amount and the voucher transaction amount
        if (mappedEditableVoucherTransactions.length >= 1) {
          const voucherTransactionToEdit = mappedEditableVoucherTransactions[0];
          const dtvtToEdit = findDtvtToEdit({
            dtvts,
            voucherTransactionToEdit,
            DealTranId,
          });
          updateAmountOnItem(mappedEditableVoucherTransactions[0], Amount);
          updateAmountOnItem(dtvtToEdit, Amount);
        } else {
          // IF there are no voucher transactions mapped to our deal transaction
          // Get all editable voucher transactions with same amount
          const editableVoucherTransactionsWithSameAmount = getVoucherTransactionsWithAmount(
            editableVoucherTransactions,
            Amount,
          );

          // IF there is one or more editable voucher transactions with the same amount
          // map to first voucher transaction with same amount
          if (editableVoucherTransactionsWithSameAmount.length >= 1) {
            const editableVoucherTransactionWithSameAmount =
              editableVoucherTransactionsWithSameAmount[0];
            dtvts.push({
              DealTranId,
              VoucherTranId:
                editableVoucherTransactionWithSameAmount.VoucherTranId,
              Amount,
              Pending: true,
            });
          } else {
            // IF no editable voucher transactions with same amount
            // take first editable voucher and map to it
            const editableVoucherTransaction = editableVoucherTransactions[0];

            dtvts.push({
              DealTranId,
              VoucherTranId: editableVoucherTransaction.VoucherTranId,
              Amount,
              Pending: true,
            });
          }
        }
      },
    );
  });

  return {
    deals,
    dealTransactions,
    dtvts,
    voucherTransactions,
    vouchers,
  };
};

export { voucherDealMap, getVoucherTypeId };
